import { type ExternalLinkMetadatum } from 'Lessons';

/*
    Vanilla replacements for the getters on the Iguana ExternalLinkMetadatum model.

    The only subtle one is isWorking. Read it carefully: a link is shown unless the crawler has something
    bad to say about it, and `verified_working` is the override a person sets by hand.

        no crawler complaint       shown, unless someone set verified_working to false
        crawler failed, or 404,
        410 or 422 came back       hidden, unless someone set verified_working to true

    So `verifiedWorking ?? true` in the good case and `!!verifiedWorking` in the bad one. Collapsing those
    two into one expression gets it wrong for a null.
*/

export const getExternalLinkErrorStatus = (link: ExternalLinkMetadatum) => link.fetchError?.status;

export const isExternalLinkNotFound = (link: ExternalLinkMetadatum) =>
    [404, 410].includes(getExternalLinkErrorStatus(link) ?? 0);

export const isExternalLinkUnprocessableEntity = (link: ExternalLinkMetadatum) =>
    getExternalLinkErrorStatus(link) === 422;

export const isExternalLinkFailedStatus = (link: ExternalLinkMetadatum) =>
    isExternalLinkNotFound(link) || isExternalLinkUnprocessableEntity(link);

export const isExternalLinkWorking = (link: ExternalLinkMetadatum) => {
    if (link.robotFailedToFetch || isExternalLinkFailedStatus(link)) return !!link.verifiedWorking;
    return link.verifiedWorking ?? true;
};

/*
    Mirrors the model's `title` getter. A title written into the lesson content with markdown's title
    syntax, `[text](url "Title")`, wins over whatever the crawler scraped.
*/
export const getExternalLinkTitle = (link: ExternalLinkMetadatum) =>
    link.linkTitleAttribute || link.metadata?.meta?.title || undefined;

// Required links first, then by sort ascending.
export const sortExternalLinks = (links: ExternalLinkMetadatum[]) =>
    [...links].sort((a, b) => {
        if (a.required !== b.required) return a.required ? -1 : 1;
        return a.sort - b.sort;
    });
